export type FitterSetupPipeSize = {
  pipeId: string;
  label: string;
  size: number | string | null;
  wallThickness: number | string | null;
  /** Non-empty group ids from setup only (empty/null omitted). */
  groupIds: string[];
};

function normalizeGroupId(value: unknown): string | null {
  const raw = String(value ?? '').trim();
  if (!raw || raw === '—' || raw.toLowerCase() === 'null' || raw.toLowerCase() === 'undefined') {
    return null;
  }
  return raw;
}

function pipeSizeLabel(pipe: Record<string, unknown>): string {
  const size = pipe.size ?? pipe.pipeSize ?? null;
  const wallThickness = pipe.wallThickness ?? null;
  const dims = `(${size ?? '—'}" × ${wallThickness ?? '—'}")`;

  // Prefer pipeName so labels are "Pipe 1 (…)" — not "Pipeline Pipe 1 (…)".
  const pipeName = String(pipe.pipeName ?? '').trim();
  if (pipeName) return `${pipeName} ${dims}`;

  const fromDisplay = String(pipe.displayName ?? '').trim();
  if (fromDisplay) {
    // Drop trailing " · groupId" and project-type prefixes (Pipeline / Others / …).
    const withoutGroup = fromDisplay.replace(/\s·\s[^\s·]+$/, '').trim();
    const withoutType = withoutGroup
      .replace(
        /^(Pipeline|Others|Buckle Arrestor|Pipe in Pipe|Bulk Head|Spools|fabrication)\s+/i,
        ''
      )
      .trim();
    return withoutType || withoutGroup || fromDisplay;
  }
  return dims;
}

/**
 * Pipe Fitter–enabled setup sizes → unique pipeId with known non-empty groupIds.
 * Pipe sizes with no groupId are omitted; callers should also hide projects with no pipes.
 */
export function buildFitterSetupTree(
  pipeSizesData: Array<Record<string, unknown>>
): FitterSetupPipeSize[] {
  const byPipeId = new Map<string, FitterSetupPipeSize>();

  for (const pipe of pipeSizesData) {
    if (!pipe.pipeFitter) continue;
    const pipeId = String(pipe.pipeId ?? '').trim();
    if (!pipeId) continue;

    const groupId = normalizeGroupId(pipe.groupId);
    // Skip rows with empty/null groupId — do not list the pipe size.
    if (!groupId) continue;

    const size = (pipe.size ?? pipe.pipeSize ?? null) as number | string | null;
    const wallThickness = (pipe.wallThickness ?? null) as number | string | null;
    const label = pipeSizeLabel(pipe);

    const existing = byPipeId.get(pipeId);
    if (!existing) {
      byPipeId.set(pipeId, {
        pipeId,
        label,
        size,
        wallThickness,
        groupIds: [groupId],
      });
      continue;
    }
    if (!existing.groupIds.includes(groupId)) {
      existing.groupIds.push(groupId);
    }
  }

  return Array.from(byPipeId.values());
}
